import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
} from "react";
import { IProducto } from "../types/producto";
import { getProductos } from "../services/productos.service";
import { INITIAL_ROWS_PER_PAGE } from "../components/Table/TableFooter";
import { borrar } from "../services/productos.service";
import { handleNewRubro } from "../services/rubros.service";
import { TenantsContext } from "../../../context/tenants.context";

interface Filtros {
  idLocal: string;
  idRubro: string;
  nombreProducto: string;
  soloActivos: boolean;
  publicadoDesde: string | null;
  publicadoHasta: string | null;
}

export interface Paginator {
  handleChangePage: (e: any, newPage: number) => void;
  handleChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
}

interface Actions {
  borrarProducto: (idProducto: number) => void;
  handleChangeEstado: (
    idProducto: number,
    funcion: Function,
    estado: string
  ) => void;
  handleChangeRubro: (
    idProducto: number,
    idRubro: number,
    nombreRubro: string
  ) => void;
}

const defaultFiltros: Filtros = {
  idLocal: "",
  idRubro: "",
  nombreProducto: "",
  soloActivos: false,
  publicadoDesde: null,
  publicadoHasta: null,
};

export const defaultPaginator: Paginator = {
  handleChangePage: () => {},
  handleChangeRowsPerPage: () => {},
};

const defaultActions: Actions = {
  borrarProducto: () => {},
  handleChangeEstado: () => {},
  handleChangeRubro: () => {},
};

interface ProductosAdminContextProps {
  productos: any[];
  productosSeleccionados: any[];
  setProductosSeleccionados: (p: any[]) => void;
  puedePausarVarios: boolean;
  setPuedePausarVarios: (puede: boolean) => void;
  puedeReanudarVarios: boolean;
  setPuedeReanudarVarios: (puede: boolean) => void;
  accionesMasivas: boolean;
  setAccionesMasivas: (accionesMasivas: boolean) => void;
  confirmDialog: any;
  setConfirmDialog: (confirm: any) => void;
  loading: boolean;
  paginator: Paginator;
  actions: Actions;
  searching: boolean;
  setSearching: (searching: boolean) => void;
  setFiltros: (filtros: Filtros) => void;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  editProductId: number;
  setEditProductId: (id: number) => void;
  idLocalEditing: number;
  setIdLocalEditing: (id: number) => void;
  clearEditing: () => void;
  handleLeft: () => void;
  fetchData: () => Promise<void>;
}

// create context
export const ProductosAdminContext = createContext<ProductosAdminContextProps>({
  productos: [],
  productosSeleccionados: [],
  setProductosSeleccionados: () => {},
  puedePausarVarios: false,
  setPuedePausarVarios: () => {},
  puedeReanudarVarios: false,
  setPuedeReanudarVarios: () => {},
  accionesMasivas: false,
  setAccionesMasivas: () => {},
  confirmDialog: "",
  setConfirmDialog: () => {},
  loading: false,
  paginator: defaultPaginator,
  actions: defaultActions,
  searching: false,
  setSearching: () => {},
  setFiltros: () => {},
  page: 0,
  setPage: () => {},
  pageSize: INITIAL_ROWS_PER_PAGE,
  setPageSize: () => {},
  editProductId: 0,
  setEditProductId: () => {},
  idLocalEditing: 0,
  setIdLocalEditing: () => {},
  clearEditing: () => {},
  handleLeft: () => {},
  fetchData: async () => {},
});

// create provider
export const ProductosAdminProvider = ({ children }: any) => {
  const [productos, setProductos] = useState<IProducto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searching, setSearching] = useState<boolean>(false);
  const [filtros, setFiltros] = useState<Filtros>(defaultFiltros);
  const [paginator] = useState<Paginator>(defaultPaginator);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(INITIAL_ROWS_PER_PAGE);
  const [actions] = useState<Actions>(defaultActions);
  const [editProductId, setEditProductId] = useState<number>(0);
  const [idLocalEditing, setIdLocalEditing] = useState<number>(0);
  const [productosSeleccionados, setProductosSeleccionados] = useState<any[]>(
    []
  );
  const [puedePausarVarios, setPuedePausarVarios] = useState<boolean>(false);
  const [puedeReanudarVarios, setPuedeReanudarVarios] =
    useState<boolean>(false);
  const [accionesMasivas, setAccionesMasivas] = useState<boolean>(false);
  const [confirmDialog, setConfirmDialog] = useState<any>({
    open: false,
    text: {
      title: "",
      body: "",
      leftButton: "Cancelar",
      rightButton: "Confirmar",
      colorButtonConfirm: "primary",
    },
    action: async () => {},
  });
  const { tenant } = useContext(TenantsContext);

  const fetchData = useCallback(async () => {
    const data = await getProductos({ ...filtros }, tenant).then(
      (res) => res.data
    );
    setProductos(data.data);
  }, [filtros]);

  const clearEditing = () => {
    setEditProductId(0);
    setIdLocalEditing(0);
  };

  // Paginator
  paginator.handleChangePage = (e: any, newPage: number) => setPage(newPage);

  paginator.handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Actions
  actions.handleChangeEstado = async (
    idProducto: number,
    funcion: Function,
    estado: string
  ) => {
    try {
      const newProductos = productos.map((producto) => {
        if (producto.idProducto === idProducto)
          return {
            ...producto,
            aprobado: estado === "Aprobado",
            rechazado: estado === "Rechazado",
            pausado: estado === "Pausado",
          };
        else return producto;
      });
      setProductos(newProductos);
      await funcion(idProducto, tenant);
    } catch (e) {
      console.log(e);
    }
  };

  actions.handleChangeRubro = async (
    idProducto: number,
    idRubro: number,
    nombreRubro: string
  ) => {
    try {
      const newProductos = productos.map((producto) => {
        if (producto.idProducto === idProducto)
          return {
            ...producto,
            idRubro,
            nombreRubro,
          };
        else return producto;
      });
      setProductos(newProductos);
      await handleNewRubro(idProducto, idRubro, tenant);
    } catch (e) {
      console.log(e);
    }
  };

  actions.borrarProducto = async (idProducto: number) => {
    try {
      setProductos(
        productos.filter((producto) => producto.idProducto !== idProducto)
      );
      await borrar(idProducto, tenant);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (!searching) return;
    setPage(0);
    setLoading(true);

    Promise.all([fetchData()])
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filtros, searching, fetchData]);

  const handleLeft = useCallback(() => {
    setProductosSeleccionados([]);
    setAccionesMasivas(false);
    setPuedePausarVarios(false);
    setPuedeReanudarVarios(false);
  }, []);

  useEffect(() => {
    if (productosSeleccionados.length <= 0) return setAccionesMasivas(false);

    setAccionesMasivas(true);

    const haySoloPausados = productosSeleccionados.every(
      (p: any) => p.pausado === true
    );

    const haySoloUnPausados = productosSeleccionados.every(
      (p: any) => p.pausado === false
    );

    setPuedePausarVarios(!haySoloPausados || haySoloUnPausados);
    setPuedeReanudarVarios(!haySoloUnPausados || haySoloPausados);
  }, [productosSeleccionados]);

  return (
    <ProductosAdminContext.Provider
      value={{
        productos,
        productosSeleccionados,
        setProductosSeleccionados,
        puedePausarVarios,
        setPuedePausarVarios,
        puedeReanudarVarios,
        setPuedeReanudarVarios,
        accionesMasivas,
        setAccionesMasivas,
        confirmDialog,
        setConfirmDialog,
        loading,
        setFiltros,
        paginator,
        page,
        setPage,
        pageSize,
        setPageSize,
        actions,
        searching,
        setSearching,
        editProductId,
        setEditProductId,
        setIdLocalEditing,
        idLocalEditing,
        clearEditing,
        handleLeft,
        fetchData,
      }}
    >
      {children}
    </ProductosAdminContext.Provider>
  );
};
